import {
  IBulkOfferingItem,
  IBulkQuoteOfferingReqSchema,
} from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { useId, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  MButton,
  MCloseButton,
  MFlex,
  MFormField,
  MGridItem,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { getOfferingNameById } from '../../quoteUtils';
import { RateItemContent } from '../quoteOffering/RateItemContent';
import { BulkOfferingProductsChecklist } from './BulkOfferingProductsChecklist';
import {
  getBillingFrequencyPhrase,
  getBulkProductsSummary,
} from './utils/bulkOfferingUtils';

export default function BulkRatesRow({
  fieldsCount,
  field,
  offerings,
  productIds,
  onProductIdsChange,
  errorMessages,
  disabled,
  control,
  index,
  itemListByOfferingId,
  billingFrequency,
  moreThanOneRate,
  handleErrorMessageValidation,
  removeOffering,
}: Readonly<{
  fieldsCount: number;
  field: { offeringId: string };
  offerings: IBulkOfferingItem[];
  productIds?: string[];
  onProductIdsChange: (productIds: string[]) => void;
  errorMessages: Record<string, string> | undefined;
  control: Control<IBulkQuoteOfferingReqSchema>;
  index: number;
  disabled?: boolean;
  itemListByOfferingId: Record<
    string,
    {
      items: {
        id: string;
        name: string;
        billingFrequencyInMonths?: number | null;
      }[];
      isBillingFrequencyMatch: boolean;
    }
  >;
  billingFrequency: string;
  moreThanOneRate: boolean;
  handleErrorMessageValidation: (offeringId: string) => void;
  removeOffering: (offeringId: string) => void;
}>) {
  const [isProductsOpen, setIsProductsOpen] = useState(false);

  const offeringName = getOfferingNameById(field.offeringId, offerings);
  const offeringProducts =
    offerings.find(({ id }) => id === field.offeringId)?.offeringProducts ?? [];
  const productsSummary = getBulkProductsSummary(offeringProducts, productIds);
  // Unique per row, since another quote group can list the same offering
  const productsChecklistId = useId();
  const selectElementVariant = errorMessages?.[field?.offeringId]
    ? 'error'
    : undefined;

  return (
    <>
      <MGridItem>
        <MText minH={8} alignContent="center">
          {offeringName}
        </MText>
        {productsSummary && (
          <MFlex align="center" gap={2}>
            <MText fontSize="xs" color="fg.muted">
              {productsSummary.label}
            </MText>
            {productsSummary.isChoosable && (
              <MButton
                variant="link"
                size="xs"
                isDisabled={disabled}
                aria-expanded={isProductsOpen}
                aria-controls={productsChecklistId}
                aria-label={`Choose products for ${offeringName}`}
                data-testid={`bulk-offering-products-toggle-${field.offeringId}`}
                onClick={() => setIsProductsOpen(!isProductsOpen)}
              >
                {isProductsOpen ? 'Hide' : 'Choose'}
              </MButton>
            )}
          </MFlex>
        )}
      </MGridItem>

      <MGridItem>
        <MStack direction="row" alignItems="center">
          <MFormField
            isRequired
            error={errorMessages?.[field?.offeringId]}
            isDisabled={disabled}
          >
            <Controller
              name={`bulkRate.${index}.rateId`}
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <MCustomSelect
                  inputProps={{
                    variant: selectElementVariant,
                  }}
                  isDisabled={disabled}
                  placeholder="Select Rate"
                  items={itemListByOfferingId[field.offeringId]?.items ?? []}
                  itemTitle="name"
                  itemValue="id"
                  onChange={(val) => {
                    onChange(val);
                    handleErrorMessageValidation(field.offeringId);
                  }}
                  renderItemContent={RateItemContent}
                  {...rest}
                />
              )}
            />
          </MFormField>
          {fieldsCount > 1 && (
            <MCloseButton
              disabled={disabled}
              aria-label={`Remove ${offeringName}`}
              onClick={() => removeOffering(field.offeringId)}
            />
          )}
        </MStack>
        {errorMessages?.[field?.offeringId] && (
          <MText color="tRed.base" fontSize="13px" mt={2}>
            {errorMessages?.[field?.offeringId]}
          </MText>
        )}

        {itemListByOfferingId[field.offeringId]?.isBillingFrequencyMatch &&
          moreThanOneRate && (
            <MText fontSize="13px" mt={2}>
              {formatInteger(
                itemListByOfferingId[field.offeringId]?.items?.length ?? 0,
              )}{' '}
              rates are billed {getBillingFrequencyPhrase(billingFrequency)}.
              Check that the right one is selected.
            </MText>
          )}
      </MGridItem>

      {isProductsOpen && (
        <MGridItem gridColumn="1 / -1">
          <BulkOfferingProductsChecklist
            id={productsChecklistId}
            offeringProducts={offeringProducts}
            productIds={productIds}
            isDisabled={disabled}
            onChange={onProductIdsChange}
          />
        </MGridItem>
      )}
    </>
  );
}
